import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaRegCopy, FaCheck, FaPlay, FaCode, FaRocket, FaBookOpen, FaMagic, FaStar, FaGem, FaCloud } from 'react-icons/fa';
import { EnvironmentProvider, useEnvironment } from './contexts/EnvironmentContext';
import {
  MAIA_ACCENT_GRAD,
  MAIA_ACCENT_TEXT,
  MAIA_ACCENT_PURPLE,
  MAIA_ACCENT_SOFT,
  MAIA_ACCENT_BORDER,
  MAIA_BORDER,
  MAIA_BORDER_LIGHT,
  MAIA_SURFACE,
  MAIA_SURFACE_SUBTLE,
  MAIA_SURFACE_MUTED,
  MAIA_TEXT_PRIMARY,
  MAIA_TEXT_SECONDARY,
  MAIA_TEXT_MUTED,
  MAIA_RADIUS_PANEL,
  MAIA_RADIUS_CARD,
  MAIA_RADIUS_CONTROL,
  MAIA_SHADOW,
  MAIA_SHADOW_BTN,
  MAIA_FONT,
  MAIA_FONT_MONO,
  MAIA_HEADER_LOGO,
} from './maiaTheme';

declare global {
  interface Window {
    environment?: string;
  }
}

const DashboardContent: React.FC = () => {
  const { config } = useEnvironment();

  type CodeSnippet = {
    id: number;
    scope: string;
    name: string;
    code: string;
    demoUrl: string;
    icon: React.ReactNode;
  };

  const codeSnippets: CodeSnippet[] = [
    {
      id: 1,
      scope: 'sharepointlite',
      name: 'SharePoint Lite Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="sharepointlite" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaCode />,
    },
    {
      id: 2,
      scope: 'sharepointliteall',
      name: 'SharePoint Lite All Sites Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="sharepointliteall" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaRocket />,
    },
    {
      id: 3,
      scope: 'maiawiki',
      name: 'maia Wiki Documentation Bot',
      code: `<div id="chat-widget-container"></div>
<script src="${config.baseUrl}/chat-widget-serve.js" scope="maiawiki" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaBookOpen />,
    },
    {
      id: 4,
      scope: 'cloudonedoc',
      name: 'CloudOne Docs Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="cloudonedoc" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaCloud />,
    },
    {
      id: 5,
      scope: 'sechelp',
      name: 'Security Policies Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="sechelp" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaStar />,
    },
    {
      id: 6,
      scope: 'gethelp',
      name: 'GTM Help Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="gethelp" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaGem />,
    },
    {
      id: 7,
      scope: 'competebot',
      name: 'Competitive Intelligence Bot',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="competebot" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaRocket />,
    },
    {
      id: 8,
      scope: 'noscope',
      name: 'General maia Chat',
      code: `<div id="chat-widget-container"></div> 
<script src="${config.baseUrl}/chat-widget-serve.js" scope="noscope" env="${config.env}"></script>`,
      demoUrl: config.baseUrl + '/widget_demo_showcase.html',
      icon: <FaMagic />,
    },
  ];

  const [copiedIds, setCopiedIds] = useState<Set<number>>(new Set());
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const copyToClipboard = async (code: string, id: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIds(new Set(copiedIds.add(id)));
      setTimeout(() => {
        copiedIds.delete(id);
        setCopiedIds(new Set(copiedIds));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    const currentSiteUrl = window.location.href;
    if (currentSiteUrl.includes('local')) {
      window.environment = 'LOCAL';
    } else if (currentSiteUrl.includes('dev')) {
      window.environment = 'DEV';
    } else if (currentSiteUrl.includes('stg')) {
      window.environment = 'STG';
    } else {
      window.environment = 'PRD';
    }
  }, []);

  const handleShowDemo = () => {
    const demoUrl = `${config.baseUrl}/widget_demo_showcase.html?env=${config.env}&scope=noscope`;
    window.open(demoUrl, '_blank');
  };

  const steps = [
    {
      number: 1,
      title: 'Select Your Widget',
      description:
        'Choose the appropriate widget based on your specific use case and knowledge domain requirements. Each widget is optimized for particular scenarios and content types.',
    },
    {
      number: 2,
      title: 'Copy & Implement',
      description:
        'Copy the provided code snippet and paste it into your HTML document where you want the chat interface to appear. The widget will automatically initialize upon page load.',
    },
    {
      number: 3,
      title: 'Configure',
      description:
        'Adjust the behavior through additional configuration parameters to match your scope and specific requirements.',
    },
  ];

  const features = [
    { icon: '📱', label: 'Responsive Design' },
    { icon: '🔒', label: 'Enterprise Security' },
    { icon: '⚡', label: 'High Performance' },
    { icon: '🔧', label: 'Customizable' },
  ];

  return (
    <>
      <GlobalStyle />
      <DashboardContainer>
        <HeaderWrapper>
          <HeaderHairline />
          <HeaderContent>
            <LogoSection>
              <LogoMark>
                <TitleLogo src={MAIA_HEADER_LOGO} alt="maia" />
              </LogoMark>
              <BrandSuffix>Widgets</BrandSuffix>
            </LogoSection>
            <HeaderActions>
              <DemoButton onClick={handleShowDemo}>
                <FaPlay />
                Live Demo
              </DemoButton>
            </HeaderActions>
          </HeaderContent>
        </HeaderWrapper>

        <WidgetsSection>
          <SectionTitleContainer>
            <SectionTitle>Available Widgets</SectionTitle>
            <SectionSubtitle>
              Choose from our premium collection of AI-powered widgets
            </SectionSubtitle>
          </SectionTitleContainer>

          <WidgetGrid>
            {codeSnippets.map((snippet, index) => (
              <WidgetCard
                key={snippet.id}
                delay={index * 0.1}
                onMouseEnter={() => setHoveredCard(snippet.id)}
                onMouseLeave={() => setHoveredCard(null)}
                isHovered={hoveredCard === snippet.id}
              >
                <CardHeader>
                  <WidgetIcon>{snippet.icon}</WidgetIcon>
                  <WidgetInfo>
                    <WidgetTitle>{snippet.name}</WidgetTitle>
                    <WidgetScope>{snippet.scope}</WidgetScope>
                  </WidgetInfo>
                </CardHeader>

                <CodeSection>
                  <CodeHeader>
                    <CodeLabel>
                      <CodeIcon>⚡</CodeIcon>
                      Embed Code
                    </CodeLabel>
                    <CodeActions>
                      <CopyButton
                        onClick={() => copyToClipboard(snippet.code, snippet.id)}
                        isCopied={copiedIds.has(snippet.id)}
                      >
                        <ButtonInner>
                          {copiedIds.has(snippet.id) ? <FaCheck /> : <FaRegCopy />}
                          {copiedIds.has(snippet.id) ? 'Copied!' : 'Copy'}
                        </ButtonInner>
                      </CopyButton>
                    </CodeActions>
                  </CodeHeader>
                  <CodeBlock>
                    <SyntaxHighlighter
                      language="html"
                      style={oneLight}
                      customStyle={{
                        margin: 0,
                        borderRadius: MAIA_RADIUS_CONTROL,
                        fontSize: '13px',
                        fontFamily: MAIA_FONT_MONO,
                        background: MAIA_SURFACE_SUBTLE,
                        border: `1px solid ${MAIA_BORDER_LIGHT}`,
                        borderLeft: `3px solid ${MAIA_ACCENT_BORDER}`,
                        padding: '20px',
                      }}
                    >
                      {snippet.code}
                    </SyntaxHighlighter>
                  </CodeBlock>
                </CodeSection>
              </WidgetCard>
            ))}
          </WidgetGrid>
        </WidgetsSection>

        <TextSection>
          <TextTitle>How to Use maia Widgets</TextTitle>
          <TextContent>
            <p>
              maia Widgets deliver powerful AI-powered conversational capabilities through an
              elegant, easy-to-implement interface. Each widget is meticulously crafted to
              provide intelligent assistance while seamlessly integrating with your existing
              digital ecosystem.
            </p>
          </TextContent>

          <StepContainer>
            {steps.map((step, index) => (
              <Step key={step.number} delay={index * 0.2}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Step>
            ))}
          </StepContainer>

          <IconContainer>
            {features.map((feature, index) => (
              <FeatureIcon key={index} delay={index * 0.1}>
                <IconCircle>{feature.icon}</IconCircle>
                <IconLabel>{feature.label}</IconLabel>
              </FeatureIcon>
            ))}
          </IconContainer>
        </TextSection>
      </DashboardContainer>
    </>
  );
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${MAIA_FONT};
    background: ${MAIA_SURFACE_SUBTLE};
    min-height: 100vh;
    line-height: 1.6;
    color: ${MAIA_TEXT_PRIMARY};
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    scroll-behavior: smooth;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${MAIA_SURFACE_MUTED};
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${MAIA_BORDER};
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${MAIA_TEXT_MUTED};
  }
`;

const slideInUp = keyframes`
  from {
    transform: translateY(40px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const DashboardContainer = styled.div`
  background: ${MAIA_SURFACE_SUBTLE};
  min-height: 100vh;
  position: relative;
`;

const HeaderWrapper = styled.header`
  background: ${MAIA_SURFACE};
  border-bottom: 1px solid ${MAIA_BORDER_LIGHT};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  position: sticky;
  top: 0;
  z-index: 100;
  animation: ${slideInUp} 0.5s ease-out;
`;

const HeaderHairline = styled.div`
  height: 3px;
  background: ${MAIA_ACCENT_GRAD};
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 20px 25px;
    flex-direction: column;
    gap: 20px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

const LogoMark = styled.div`
  height: 46px;
  overflow: hidden;
  flex-shrink: 0;
  line-height: 0;
`;

const TitleLogo = styled.img`
  height: 62px;
  width: auto;
  display: block;
  object-position: top center;
`;

const BrandSuffix = styled.span`
  font-size: 1.75rem;
  font-weight: 500;
  color: ${MAIA_TEXT_SECONDARY};
  line-height: 1;
  letter-spacing: -0.02em;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const DemoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border: none;
  border-radius: ${MAIA_RADIUS_CONTROL};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
  background: ${MAIA_ACCENT_GRAD};
  color: white;
  box-shadow: ${MAIA_SHADOW_BTN};

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const WidgetsSection = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 50px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 40px 25px;
  }
`;

const SectionTitleContainer = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${MAIA_TEXT_PRIMARY};
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${MAIA_TEXT_SECONDARY};
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const WidgetCard = styled.div<{ delay: number; isHovered: boolean }>`
  position: relative;
  background: ${MAIA_SURFACE};
  border: 1px solid ${MAIA_BORDER_LIGHT};
  border-radius: ${MAIA_RADIUS_PANEL};
  overflow: hidden;
  box-shadow: ${MAIA_SHADOW};
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  animation: ${slideInUp} 0.5s ease-out ${(props) => props.delay}s both;

  &:hover {
    transform: translateY(-2px);
    border-color: ${MAIA_ACCENT_PURPLE};
    box-shadow: 0 24px 56px -20px rgba(15, 23, 42, 0.24), 0 6px 16px -8px rgba(15, 23, 42, 0.12);
  }
`;

const CardHeader = styled.div`
  padding: 28px 28px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 28px;
    right: 28px;
    height: 1px;
    background: ${MAIA_BORDER_LIGHT};
  }
`;

const WidgetIcon = styled.div`
  width: 44px;
  height: 44px;
  background: ${MAIA_ACCENT_SOFT};
  border-radius: ${MAIA_RADIUS_CONTROL};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MAIA_ACCENT_TEXT};
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const WidgetInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const WidgetTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${MAIA_TEXT_PRIMARY};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const WidgetScope = styled.p`
  font-size: 0.85rem;
  color: ${MAIA_TEXT_SECONDARY};
  font-family: ${MAIA_FONT_MONO};
  background: ${MAIA_SURFACE_MUTED};
  padding: 4px 10px;
  border-radius: 8px;
  display: inline-block;
  border: 1px solid ${MAIA_BORDER_LIGHT};
  margin: 0;
`;

const CodeSection = styled.div`
  padding: 0 28px 28px;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-top: 16px;
`;

const CodeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${MAIA_TEXT_SECONDARY};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CodeIcon = styled.span`
  font-size: 1rem;
`;

const CodeActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CopyButton = styled.button<{ isCopied: boolean }>`
  border: 1px solid
    ${(props) => (props.isCopied ? MAIA_ACCENT_BORDER : 'rgba(15, 23, 42, 0.09)')};
  background: ${(props) => (props.isCopied ? MAIA_ACCENT_SOFT : MAIA_SURFACE)};
  border-radius: ${MAIA_RADIUS_CONTROL};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${MAIA_ACCENT_BORDER};
    color: ${MAIA_ACCENT_TEXT};
    transform: translateY(-1px);
  }
`;

const ButtonInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-weight: 500;
  font-size: 0.85rem;
  color: ${MAIA_TEXT_PRIMARY};
`;

const CodeBlock = styled.div`
  border-radius: ${MAIA_RADIUS_CONTROL};
  overflow: hidden;
`;

const TextSection = styled.section`
  max-width: 1400px;
  margin: 0 auto 60px;
  padding: 48px 50px;
  background: ${MAIA_SURFACE};
  border: 1px solid ${MAIA_BORDER_LIGHT};
  border-radius: ${MAIA_RADIUS_PANEL};
  box-shadow: ${MAIA_SHADOW};

  @media (max-width: 768px) {
    padding: 36px 25px;
    margin: 0 25px 40px;
  }
`;

const TextTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: ${MAIA_TEXT_PRIMARY};
  margin-bottom: 24px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const TextContent = styled.div`
  font-size: 1.05rem;
  color: ${MAIA_TEXT_SECONDARY};
  line-height: 1.8;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 48px;

  p {
    margin-bottom: 0;
  }
`;

const StepContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 48px 0;
`;

const Step = styled.div<{ delay: number }>`
  background: ${MAIA_SURFACE_SUBTLE};
  border-radius: ${MAIA_RADIUS_CARD};
  padding: 28px;
  border: 1px solid ${MAIA_BORDER_LIGHT};
  transition: transform 0.25s ease, border-color 0.25s ease;
  animation: ${fadeIn} 0.5s ease-out ${(props) => props.delay}s both;

  &:hover {
    transform: translateY(-2px);
    border-color: ${MAIA_ACCENT_PURPLE};
  }
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  background: ${MAIA_ACCENT_GRAD};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 20px;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${MAIA_TEXT_PRIMARY};
  margin-bottom: 12px;
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: ${MAIA_TEXT_SECONDARY};
  line-height: 1.7;
  margin: 0;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 48px;
  margin-top: 48px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 32px;
  }
`;

const FeatureIcon = styled.div<{ delay: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 120px;
  animation: ${fadeIn} 0.5s ease-out ${(props) => props.delay}s both;
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  background: ${MAIA_ACCENT_SOFT};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 1.75rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const IconLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${MAIA_TEXT_SECONDARY};
  text-align: center;
  line-height: 1.4;
`;

const Dashboard: React.FC = () => {
  const [currentEnv, setCurrentEnv] = useState<string>('PRD');

  useEffect(() => {
    const currentSiteUrl = window.location.href;
    let detectedEnv = 'PRD';

    if (currentSiteUrl.includes('local')) {
      detectedEnv = 'LOCAL';
    } else if (currentSiteUrl.includes('dev')) {
      detectedEnv = 'DEV';
    } else if (currentSiteUrl.includes('stg')) {
      detectedEnv = 'STG';
    } else {
      detectedEnv = 'PRD';
    }

    setCurrentEnv(detectedEnv);
    window.environment = detectedEnv;
  }, []);

  return (
    <EnvironmentProvider env={currentEnv} scope="noscope">
      <DashboardContent />
    </EnvironmentProvider>
  );
};

export default Dashboard;
